<script setup lang="ts">
/**
 * 核可「自助申請代管 CS」的對話框（批81 需求 5、裁示 14 修訂版）。
 *
 * 與一般核發框的差別：這一按下去會**真的開一整套站**（建 host_sites、簽證、建 DNS、
 * 派 provision_site job），所以多了「選主機＋逐台容量 preflight」那一段。
 *
 * 兩個唯讀區塊是刻意的：
 * - **slug／網域**：客戶在 portal 填的，核可時只能看。要改就婉拒請他重送——否則他收到的
 *   網址跟自己申請的不一樣。
 * - **客戶 setup 資訊**：只顯示站台名稱／管理員／Email，**沒有密碼**（LS 手上也只有
 *   bcrypt hash，明碼從頭到尾沒有離開客戶的瀏覽器）。
 *
 * ⚠️ `ModulePicker`／`QuotaEditor` 的 `initFrom` **只在掛載時跑** → 套用套裝之後必須換
 * `:key` 重掛，否則畫面停在舊值而送出的是新值（既有 ProvisionView／RequestsView 同一招）。
 */
import { computed, ref, watch } from 'vue'

import ModulePicker from '@/components/ModulePicker.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import SiteSecretInputs from '@/components/SiteSecretInputs.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { ManagedHost, ProvisionPreflight } from '@/api/hosting'
import * as ls from '@/api/ls'
import type { LicenseReq, TrialPreset } from '@/api/ls'
import * as reseller from '@/api/reseller'
import { vEnter } from '@/lib/enterKey'
import { hostStatusLabel } from '@/lib/fleet'
import { featuresOf, hasFeature, rawProfileOf } from '@/lib/hostingProfile'
import { hostedContentBlocked, hostedContentHint, hostedContentReason } from '@/lib/hostedContent'
import { issuablePlans, planByKey, termLabel, termOptionsWith, trialPlanOf } from '@/lib/products'
import { quotaSummaryText } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import {
  SITE_SECRET_ERROR_MESSAGES,
  declaredSecretFields,
  filledSecrets,
  provisionSecretBlocker,
} from '@/lib/siteSecrets'
import { MINUTES_PER_DAY, splitMinutes, totalMinutes } from '@/lib/terms'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'

const props = defineProps<{ request: LicenseReq | null; customerName: string }>()
const emit = defineEmits<{ close: []; done: [] }>()

const dialogs = useDialogsStore()
const store = useDataStore()
/**
 * 批99-B：這個框**兩個 realm 共用**。
 *
 * 核可／婉拒／試用初值三支走 `api/hosting.ts` 的 `base()`（代理商自動變成
 * `/api/admin/reseller/...`）；撤回那支兩邊簽章不同（代理商版不收理由），所以在
 * `doCancel()` 裡分流。主機清單也不同：代理商後端只回自家主機，我方要自己濾
 * （見 `visibleHosts`）。
 */
const auth = useAuthStore()
/**
 * 這筆申請要開的產品（批99-A）——**一律以申請單為準**（`request.product`），
 * 這裡不給選也不猜。
 *
 * 批99-A 之前這裡是硬編常數 `iplusone-cs`（那時整條代管管線只服務 CS）。改成讀申請單
 * 之後，客戶在 portal 送的是哪個產品、我方核可時簽的就是哪個；要換產品只能請客戶重送，
 * 否則他收到的站會與自己申請的不是同一套系統。
 */
const productKey = computed(() => props.request?.product ?? '')
/** 產品中文名（憑證設計還沒載回來時退回原始鍵，不留空白）。 */
const productName = computed(() => store.productByKey(productKey.value)?.name || productKey.value || '—')
/** 代管產品的憑證設計（模組／配額編輯器與套裝 chip 的來源）。 */
const spec = computed(() => store.productByKey(productKey.value))
/**
 * 套裝 chip 的清單＝可販售 ∪ **試用模板**（`issuablePlans`），**我方與代理商都一樣**。
 *
 * ★ 批112 修正：原本我方視角只列 `sellablePlans`、把試用當「客製＋代管試用初值」——
 *   主人 09-17 指正「portal 申請試用後，審核變客製這樣也不對」。試用就是一個套裝
 *   （`plan=trial`、證標 `trial`），只是代管的期限用產品選項 `hosted_trial_term_days`
 *   （30 天）而不是模板本身的 90 天。代理商仍受後端核發政策閘（`activation_policy`）管。
 */
// 批155：內部套裝只有營運主管以上列得出來（代理商恆不列）。
const plans = computed(() => issuablePlans(spec.value, { internal: auth.isManagerOrAbove }))
/**
 * 這個套裝這個角色能不能直接拿來核可開站——依角色＋偏離判斷（批99-B 立、批151 裁示 50 改）。
 *
 * - 代理商：只能照 `activation_policy === 'reseller'` 的套裝模板簽（其餘灰掉並說明去處）。
 * - 我方：開站內容閘不看套裝本身的 `activation_policy`，套裝 chip 一律可按；偏離套裝或沒選
 *   套裝才要營運主管——那是**內容**層的判斷（`contentReason`／`contentBlocked`），admin
 *   偏離時提前提示並擋住「核可並開站」，不要按了才 403。
 */
function planDirectOk(policy: string | null | undefined): boolean {
  return !auth.isReseller || policy === 'reseller'
}
/** 「客製」chip：代理商不行；我方要營運主管以上（沒選套裝＝開站內容閘的 `no_plan`）。 */
const customPlanOk = computed(() => !auth.isReseller && !hostedContentBlocked(auth.role, 'no_plan'))
/**
 * 期限下拉的選項（**代理商視角專用**）。
 *
 * 後端 `activation._term_allowed` 允許的是「套裝自己的期限 ∪ 產品的可選期限」——
 * CS 的試用是 90 天、產品選項是 30／365，所以代理商可選的其實是這三個。我方視角維持
 * 原本寫死的四個（不受政策閘，行為一位元不變）。
 * ★ 批108：單位一律**總分鐘**（`term_days * 1440 + term_minutes`）。
 */
const termChoices = computed(() => {
  const p = planByKey(spec.value, plan.value)
  return termOptionsWith(spec.value, p ? totalMinutes(p.term_days, p.term_minutes) : null)
})

const open = computed(() => !!props.request)
/**
 * 09-02 主人裁示：清單列上只留「檢視」，核可與婉拒都在這個框裡。
 * 已審核過的申請照樣開得起來，但**唯讀**（不顯示主機選擇與授權表單、footer 只有關閉）。
 */
const isPending = computed(() => props.request?.status === 'pending')
/**
 * 草稿狀態的代管申請（批97 之前的死單）：`return` 當年沒有來源閘，客戶自助送的
 * 開站申請一被退回就變成 draft——而客戶端沒有草稿介面、我方當時也撤不動。
 * 這個框要看得到它們，也要撤得動。
 */
const isDraft = computed(() => props.request?.status === 'draft')
/** 客戶在 portal 填的（唯讀顯示）。 */
const slug = computed(() => String(props.request?.request_json?.slug ?? ''))
const setup = computed(
  () =>
    (props.request?.request_json?.setup ?? {}) as {
      site_name?: string
      admin_name?: string
      admin_email?: string
      login?: string
    },
)
/**
 * 客戶選的登入方式（主人 09-02 裁示 1）：密碼是選填，沒填＝那位管理員用 Google 帳號登入。
 * 舊列（09-02 之前送出的）沒有 `login` 鍵，但它們一定有密碼 → 顯示「密碼」，與後端
 * `approve_hosted_request` 的推導逐字相同。
 */
const loginLabel = computed(() =>
  setup.value.login === 'google' ? 'Google 帳號（未設密碼）' : '帳號密碼',
)
/** 與後端同一套推導：`login` 缺席時看有沒有密碼雜湊（這裡看不到雜湊，舊列一律是密碼）。 */
const loginMethod = computed(() => (setup.value.login === 'google' ? 'google' : 'password'))

// --- 主機選擇＋容量 preflight ---
const hosts = ref<ManagedHost[]>([])
const hostId = ref('')
const pre = ref<ProvisionPreflight | null>(null)
const preBusy = ref(false)
const preError = ref('')

/**
 * 這個產品的代管功能旗標（批141）——決定說明文字要講「會開反向隧道／代管 Google 登入」
 * 還是「這個產品不用」。來源依序：選主機後的 preflight（權威，兩個 realm 都有）→ 憑證
 * 設計裡的代管設定（我方視角）。`undefined`＝還不知道、`null`＝產品沒有有效的代管設定。
 */
const profileFeatures = computed<Record<string, boolean> | null | undefined>(() => {
  if (pre.value?.profile) return pre.value.profile.features ?? null
  // 批151 R1-16：代理商拿到的產品投影沒有代管設定（白名單），從那裡讀會誤判成「沒有有效的代管設定」
  // ——代理商一律等 preflight（選主機後）才知道。
  if (spec.value && !auth.isReseller) return featuresOf(spec.value.options)
  return undefined
})
/** 產品代管設定允許的登入方式（我方視角才看得到；看不到＝null，不做前端判斷）。 */
const allowedLogins = computed<string[] | null>(() => {
  const raw = rawProfileOf(spec.value?.options) as { login_methods?: unknown } | null | undefined
  return raw && Array.isArray(raw.login_methods) ? (raw.login_methods as string[]) : null
})
/** 客戶選的登入方式不在產品允許的範圍內＝核可一定被後端 409 `login_method_unsupported`。 */
const loginUnsupported = computed(
  () => allowedLogins.value != null && !allowedLogins.value.includes(loginMethod.value),
)

// --- 授權表單（比照 ProvisionView 第 4 步）---
const trial = ref(true)
/** 期限的**總分鐘**（批108）；送出前用 `splitMinutes` 拆成兩欄。 */
const termTotal = ref(30 * MINUTES_PER_DAY)
const plan = ref('')
const modules = ref<Record<string, string>>({})
/**
 * 數量配額（批114 起是**唯一**的數量入口；獨立的數字欄已拔除）。
 * `quotasValid` 是 `QuotaEditor` 回報的必填檢查——起手 true，理由見 `RequestsView`。
 */
const quotas = ref<Record<string, number | null> | null>(null)
const quotasValid = ref(true)
const pickerKey = ref(0)
const quotaKey = ref(0)

const trialDefaults = ref<TrialPreset | null>(null)
const submitting = ref(false)

/**
 * **開站內容閘**（批151 裁示 50）：我方視角才算（代理商有自己的政策閘）。admin 偏離套裝或
 * 沒選套裝＝核可必 403 → 提前擋住按鈕並說明；營運主管以上只提示。
 */
const contentReason = computed(() => {
  if (auth.isReseller) return null
  return hostedContentReason(spec.value, planByKey(spec.value, plan.value || null), {
    modules: modules.value,
    quotas: quotas.value,
    term_days: splitMinutes(termTotal.value)[0],
    term_minutes: splitMinutes(termTotal.value)[1],
    trial: trial.value,
  })
})
const contentBlocked = computed(() => hostedContentBlocked(auth.role, contentReason.value))
const contentHint = computed(() => hostedContentHint(contentReason.value, contentBlocked.value))

// --- 站台機密（批151 C6；營運主管以上、產品有宣告才出現；只在最後送出時帶）---
/**
 * 站台機密的明文（只活在這個 ref 裡）。★ 開窗、關窗（任何方式）、送出成功都清空——
 * 見 `clearSecrets()` 的呼叫點；批151 R3-14 之前只有開窗時清，關掉之後值還留在元件狀態。
 */
const secretValues = ref<Record<string, string>>({})
function clearSecrets() {
  secretValues.value = {}
}
/** 欄位宣告：選主機後的 preflight（權威）→ 產品代管設定（我方視角的退路）。 */
const secretFields = computed(
  () => pre.value?.site_secret_fields ?? declaredSecretFields(rawProfileOf(spec.value?.options)),
)
const showSecrets = computed(() => !auth.isReseller && auth.isManagerOrAbove && secretFields.value.length > 0)
/** 這台主機帶不帶得了（`null`＝可以）——要先選主機、跑完容量檢查才知道。 */
const secretBlocker = computed(() =>
  provisionSecretBlocker(
    pre.value && pre.value.site_secret_fields ? pre.value : null,
    '先選主機（容量檢查完成後）才知道這台主機能不能帶站台機密。',
  ),
)
const secretsToSend = computed(() =>
  showSecrets.value && !secretBlocker.value ? filledSecrets(secretValues.value) : null,
)
const secretsValid = computed(() =>
  Object.values(secretsToSend.value ?? {}).every((v) => v.length >= 8 && v.length <= 512),
)

// --- 婉拒（09-02：**同框展開**，不再開第二層 modal）---
// 多疊一層 modal 只是讓操作者離申請內容更遠，而婉拒正是最需要對著內容決定的動作。
const rejectOpen = ref(false)
const rejectReason = ref('')
const rejecting = ref(false)
async function doReject() {
  if (!props.request) return
  rejecting.value = true
  try {
    // 走 `base()`：代理商打 `/api/admin/reseller/license-requests/{id}/reject`
    // （自家客戶的代管申請可婉拒，理由客戶看得到）。
    await hosting.rejectHostedRequest(props.request.id, { reason: rejectReason.value.trim() || null })
    rejectOpen.value = false
    clearSecrets()
    emit('done')
  } catch (e) {
    await dialogs.notify({ title: '婉拒失敗', message: errText(e, '婉拒失敗') })
  } finally {
    rejecting.value = false
  }
}

// --- 撤回（作廢；批97）------------------------------------------------------
// 「婉拒」是對客戶的答覆（終態、對外），「撤回」是把單子從流程裡拿掉。draft 狀態的
// 代管申請沒有婉拒路徑（`reject` 只受理 pending），撤回是它唯一的出口。
const cancelOpen = ref(false)
const cancelReason = ref('')
const cancelling = ref(false)
/**
 * 撤回（作廢）。
 *
 * ⚠️ 批99-B：代理商 realm 的 `POST /reseller/license-requests/{id}/cancel` 撤的是
 *   **自家公司送出的申請**（`license_requests.reseller_id == 我`）。客戶自己在 portal
 *   送的代管申請那一欄是 NULL（B.3a 刻意不寫入，以保住「由代理商送」的語意），
 *   所以代理商對這些單子只有「婉拒」這條路。硬給他一顆撤回鈕只會 404。
 */
const canCancel = computed(() => {
  if (!isPending.value && !isDraft.value) return false
  if (!auth.isReseller) return true
  return (props.request?.origin ?? '') === 'reseller'
})
/**
 * 代管開站申請一律是客戶自助（portal）送的 → 理由必填（客戶看得到）。
 * 代理商視角只撤得動自己送的單子，後端那支不收理由 → 不必填。
 */
const cancelReasonRequired = computed(
  () => !auth.isReseller && (props.request?.origin || 'portal') !== 'admin',
)
async function doCancel() {
  if (!props.request) return
  const reason = cancelReason.value.trim()
  if (cancelReasonRequired.value && !reason) return
  cancelling.value = true
  try {
    // 代理商版不收理由（後端簽章沒有 body）——撤的是自家送的單子，理由欄對他不存在。
    if (auth.isReseller) await reseller.cancelRequest(props.request.id)
    else await ls.cancelLicenseRequest(props.request.id, reason ? { reason } : undefined)
    cancelOpen.value = false
    clearSecrets()
    emit('done')
  } catch (e) {
    await dialogs.notify({ title: '撤回失敗', message: errText(e, '撤回失敗') })
  } finally {
    cancelling.value = false
  }
}

/** 已審核過的列：交代結果（狀態／核發的授權／審核理由）。 */
function statusLabel(st: string | undefined): string {
  // `draft` 是批97 之前「退回代管申請」留下的死單狀態（見 `isDraft`）——少了這個鍵
  // 會把原始英文字印在畫面上。
  return { draft: '草稿', pending: '審核中', approved: '已核發', rejected: '已婉拒', cancelled: '已取消' }[st ?? ''] ?? (st ?? '—')
}
const outcomeRows = computed<{ k: string; v: string }[]>(() => {
  const r = props.request
  if (!r || r.status === 'pending') return []
  const out = [{ k: '狀態', v: statusLabel(r.status) }]
  if (r.issued_license_id) out.push({ k: '核發的授權', v: r.issued_license_id })
  const review = (r.request_json?.review ?? null) as { reason?: string | null } | null
  // 這個鍵三種動作共用（婉拒／退回／批97 起的撤回）→ 標題不能寫死「婉拒理由」。
  if (review?.reason) out.push({ k: '審核理由', v: String(review.reason) })
  const site = (r.request_json?.approved_site ?? null) as { domain?: string } | null
  if (site?.domain) out.push({ k: '開出來的站', v: site.domain })
  const cancel = (r.request_json?.cancel ?? null) as { at?: string; by?: string } | null
  if (cancel?.at) {
    out.push({ k: '取消時間', v: `${cancel.at.replace('T', ' ').slice(0, 16)}（${cancel.by ?? '—'}）` })
  }
  return out
})

/** 開站失敗的錯誤碼 → 給操作者看的一句話（與 `ProvisionView` 同一組措辭）。 */
const ERROR_MESSAGES: Record<string, string> = {
  activation_policy_required:
    '這份內容不在代理商可免審核發的套裝範圍，請改走授權申請流程——我方審核核發後會自動派到站上。',
  host_reseller_mismatch: '這位客戶不屬於這台主機的代理商，請改選一台自營主機或該代理商的主機。',
  product_not_hostable: '這個產品目前沒有開放由我方代管。',
  host_product_unsupported: '這台主機的 agent 不支援這個產品（可能要先升級 agent）。',
  product_out_of_scope: '這個產品不在您的產品範圍內。',
  hosting_unavailable: '您的產品範圍內沒有開放代管的產品。',
  // 批141：依產品的代管設定。
  hosting_profile_missing: '這個產品還沒有有效的代管設定，暫時開不了站（請營運主管到〈產品〉的「代管設定」頁籤補齊）。',
  login_method_unsupported:
    '客戶選的管理員登入方式不在這個產品允許的範圍內——請婉拒並請客戶依產品規定（例如設定密碼）重新申請。',
  // 批151 C6：帶站台機密時的錯誤碼（`role_required` 照後端原文：可能是機密閘也可能是內容閘）。
  ...SITE_SECRET_ERROR_MESSAGES,
}

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return ERROR_MESSAGES[d] ?? d
  const code = (d as { code?: unknown }).code
  if (typeof code === 'string' && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]
  const m = (d as { message?: unknown }).message
  return typeof m === 'string' ? m : JSON.stringify(d)
}

/** 套用試用初值（開窗預設；勾回「試用授權」時也走這裡）。 */
function applyTrial(p: TrialPreset) {
  // 批112 修正：試用**就是**試用套裝（`plan=trial`），我方與代理商一致——不再把它留空當
  // 「客製」。初值本身就是從那張模板展開的（只有期限換成代管的 `hosted_trial_term_days`），
  // 指過去不會改變任何欄位的值；後端也會因 `kind === 'trial'` 把證標成 trial。
  plan.value = trialPlanOf(spec.value)?.key ?? ''
  // ★ 代管試用初值的 `term_days` 來自產品選項 `hosted_trial_term_days`（天）。
  termTotal.value = totalMinutes(p.term_days, p.term_minutes)
  modules.value = { ...p.modules }
  // 批114：初值的相容數字欄併進配額包（只讀 `quotas` 會讓那些數字在套用初值時消失）。
  quotas.value = seedQuotas(spec.value, p)
  pickerKey.value++
  quotaKey.value++
}

/** 套用正式套裝 → **自動取消「試用授權」**（可手動勾回）。 */
function applyPlan(key: string) {
  plan.value = key
  const p = plans.value.find((x) => x.key === key)
  // ★ 批99-B：**試用模板**也在 chip 列裡（代理商唯一按得到的就是它）——按它不該把
  //   「試用授權」取消掉，否則簽出來的是一張不會到期停站的正式證。正式套裝維持原行為。
  trial.value = p?.kind === 'trial'
  if (!p) return
  // 批112 修正：按試用套裝＝整包套代管試用初值（期限是 `hosted_trial_term_days` 的 30 天，
  // 不是模板自己的 90 天）；初值還沒載回來才退回模板內容。
  if (p.kind === 'trial' && trialDefaults.value) {
    applyTrial(trialDefaults.value)
    return
  }
  if (Object.keys(p.modules).length) modules.value = { ...p.modules }
  // 批114：模板的相容數字欄併進配額包（同 `applyTrial`）。
  quotas.value = seedQuotas(spec.value, p)
  pickerKey.value++
  quotaKey.value++
}

/**
 * 關窗（〈關閉〉、×、Esc、點背景都走這裡）：先清掉站台機密的明文再通知宿主——不依賴宿主
 * 一定會把 `request` 清成 null（那條路由 `watch(open)` 兜底）。
 */
function close() {
  clearSecrets()
  emit('close')
}

function onToggleTrial() {
  // 勾回試用＝整包換回試用初值（否則會出現「勾了試用、配額卻是正式版」的矛盾表單）
  if (trial.value && trialDefaults.value) applyTrial(trialDefaults.value)
}

async function runPreflight() {
  if (!hostId.value || !props.request) return
  preBusy.value = true
  preError.value = ''
  try {
    pre.value = await hosting.preflightHostSite(hostId.value, {
      customer_id: props.request.customer_id,
      // 批99-A：產品跟著申請單走（代管申請一定有；批114 起後端沒有預設產品可退）。
      product: productKey.value || undefined,
      slug: slug.value,
      // 批114：不再送獨立的數量欄——preflight 看的是主機容量（站數／記憶體／磁碟／LE 額度），
      // 與這張證的配額無關。
      quotas: quotas.value,
      domain_mode: 'ours',
    })
  } catch (e) {
    pre.value = null
    preError.value = errText(e, '容量檢查失敗')
  } finally {
    preBusy.value = false
  }
}

watch(hostId, () => void runPreflight())

// 開窗時準備資料：主機清單、套裝、試用初值（**帶入值都備妥後元件才掛載**——
// UiModal 內容是 v-if，ModulePicker 的 initFrom 只在掛載時跑）。
watch(
  open,
  async (isOpen) => {
    // ★ 關窗（取消、×、Esc、點背景、核可／婉拒／撤回成功後宿主把 request 清掉）一律清空
    //   站台機密的明文——不只開窗時清，否則關掉之後值還留在元件狀態裡。
    if (!isOpen) {
      clearSecrets()
      return
    }
    hostId.value = ''
    pre.value = null
    preError.value = ''
    rejectOpen.value = false
    rejectReason.value = ''
    cancelOpen.value = false
    cancelReason.value = ''
    trial.value = true
    clearSecrets()
    const [tp] = await Promise.all([
      hosting.hostedTrialPreset(productKey.value).catch(() => null),
      // 套裝與模組／配額定義都在憑證設計裡（`GET /products` 一次回齊）。
      store.ensureProducts().catch(() => {}),
      // 批99-B：主機下拉要濾掉「別家代理商的主機」，判準是**申請人的**歸屬代理商
      // ——那只在客戶列裡（`customers.reseller_id`），所以這裡要先確保載過。
      store.ensureCustomers().catch(() => {}),
    ])
    if (tp) {
      trialDefaults.value = tp
      applyTrial(tp)
    }
    // 只列可用主機（退役的選了也開不成）
    hosts.value = await hosting.listManagedHosts().catch(() => [])
  },
  { immediate: true },
)

/**
 * 申請人的歸屬代理商（批99-B）——`null`＝直營客戶。
 *
 * ⚠️ **不是** `request.reseller_id`：那一欄的語意是「這筆申請由代理商送」（`origin=reseller`
 *   才有值），而客戶自助送的代管申請恆為 NULL。我們要問的是「這個**客戶**掛在誰名下」，
 *   答案在客戶列。
 * ⚠️ 客戶清單載不到（403／網路）時這裡是 null → 下拉只剩自營主機（保守側），後端的
 *   422 `host_reseller_mismatch` 是真正的防線。
 */
const customerResellerId = computed<string | null>(() => {
  const cid = props.request?.customer_id
  if (!cid) return null
  return store.customers.find((c) => c.id === cid)?.reseller_id ?? null
})

/**
 * 下拉可選的主機。
 *
 * - **代理商視角**：後端只回自家主機，直接用（再濾一次也只是重複後端的工作）。
 * - **我方視角**：我方自營主機（`reseller_id` 為 null）∪ **該客戶的代理商**的主機。
 *   別家代理商的主機選了也是 422 `host_reseller_mismatch`，不如不要出現在清單裡。
 */
const visibleHosts = computed(() => {
  if (auth.isReseller) return hosts.value
  const rid = customerResellerId.value
  return hosts.value.filter((h) => !h.reseller_id || h.reseller_id === rid)
})

/** 下拉選項上的歸屬標記（我方視角才有意義；自營不標——多數是自營，全標等於沒標）。 */
function hostOwnerTag(h: ManagedHost): string {
  if (auth.isReseller || !h.reseller_id) return ''
  return `・代理商：${h.reseller_name ?? '—'}`
}

/** 這台主機不能用的理由（空＝可用）。與後端 preflight 的 `issues` 同一組判準。 */
function hostBlockers(h: ManagedHost): string[] {
  const out: string[] = []
  if (h.status !== 'active') out.push(`狀態為「${hostStatusLabel(h.status)}」`)
  if (!h.agent_enrolled) out.push('agent 尚未註冊')
  // 批99-A：這裡的產品是申請單固定的（不像開站精靈會跟著主機收斂），所以直接拿它比對
  // agent 自報的能力——不支援就是這台開不成，選了也只會被後端 422 `host_product_unsupported`。
  // ★ 批114 裁示 ㉟：`agent_products` 為 **null＝這台 agent 還沒回報過**（舊版 agent 的
  //   「視同某個產品」相容規則已拿掉）。那不是「不支援」，是「不知道」——提示要指路去升級，
  //   否則操作者只會一直換主機試。
  if (h.agent_products == null) out.push('agent 尚未回報支援的產品，請升級 agent')
  else if (productKey.value && !h.agent_products.includes(productKey.value)) {
    out.push(`這台 agent 不支援「${productName.value}」`)
  }
  if (h.exclusive && h.exclusive_customer_id && h.exclusive_customer_id !== props.request?.customer_id) {
    out.push(`已獨佔給「${h.exclusive_customer_name ?? '其他客戶'}」`)
  }
  // 批141（主人裁示）：**獨佔主機＝整台只開一站**——上面已經有站（含開站失敗留下的殘列）
  // 就不能再開，即使是同一位客戶。後端 preflight 同一條 issue、開站回 409 `exclusive_violation`。
  if (h.exclusive && h.sites_count > 0) {
    out.push('獨佔主機已有站台（獨佔主機整台只開一站）')
  }
  if (h.max_sites != null && h.sites_count >= h.max_sites) out.push('站數已達上限')
  return out
}

const canSubmit = computed(
  () =>
    isPending.value &&
    !rejectOpen.value &&
    !cancelOpen.value &&
    !!hostId.value &&
    !!pre.value?.ok &&
    // 批114：必填配額沒填就先擋——後端簽發會回 422 `quotas_invalid`，而這條路徑一失敗
    // 就是「已經開了一半的站」要回滾，不如在按下去之前擋住。
    quotasValid.value &&
    // 批151：admin 偏離套裝／客製＝開站內容閘必 403。
    !contentBlocked.value &&
    secretsValid.value &&
    !submitting.value,
)

async function submit() {
  if (!props.request || !hostId.value) return
  // 批114 裁示 ⑰：配額摘要用憑證設計的標籤組字（沒有宣告配額的產品回空字串＝整段省略）。
  const quotaText = quotaSummaryText(spec.value, quotas.value)
  const secrets = secretsToSend.value
  const secretCount = secrets ? Object.keys(secrets).length : 0
  const ok = await dialogs.confirm({
    title: '確認核可並開站',
    message:
      `即將在主機「${visibleHosts.value.find((h) => h.id === hostId.value)?.name}」為客戶` +
      `「${props.customerName}」開站 ${pre.value?.domain ?? slug.value}，並簽發一張` +
      `${quotaText ? `${quotaText}、` : ''}${termLabel(termTotal.value)}${trial.value ? '的試用' : '的正式'}授權` +
      `${secretCount ? `，同時設定 ${secretCount} 項站台機密` : ''}。` +
      `開站會實際安裝服務、建 DNS 與申請憑證，站台起來後會自動建好客戶填的管理員帳號。`,
    confirmText: '核可並開站',
  })
  if (!ok) return
  submitting.value = true
  try {
    const res = await hosting.approveHostedRequest(props.request.id, {
      host_id: hostId.value,
      trial: trial.value,
      term_days: splitMinutes(termTotal.value)[0],
      term_minutes: splitMinutes(termTotal.value)[1],
      plan: plan.value || null,
      modules: modules.value,
      // ★ 批114：**原樣送整包**（含值為 `null` 的「無上限」）。這裡原本會把非數字的鍵
      //   整個濾掉——等於把操作者明示的「不限」偷偷改成「沿用模板」，而畫面上看不出來。
      quotas: quotas.value,
      // 批151 C6：站台機密只在這一次送出時帶（沒填＝整個鍵不帶）。
      ...(secrets ? { site_secrets: secrets } : {}),
    })
    clearSecrets()
    emit('done')
    // 批141：含一次性權杖的備援網址（樣板帶 `{setup_token}` 的產品）只出現在這一次回應。
    // 批141 M 包：一律照後端的 `setup_url_one_time`——這個元件兩個 realm 共用，代理商視角
    // 讀不到產品的代管設定，前端不再自己猜網址長相。
    const backup =
      res.setup_url && res.setup_url_one_time
        ?'\n\n備援用的初始設定網址（含一次性權杖，只顯示這一次；站台自動建立管理員失敗時才需要）：\n' +
          res.setup_url
        : ''
    await dialogs.notify({
      title: '已核可，開站進行中',
      message:
        `站台 ${res.site.domain ?? slug.value} 正在安裝（約 10–20 分鐘）。` +
        (loginMethod.value === 'google'
          ? '完成後客戶用他的 Google 帳號登入，不需要跑初始設定精靈。'
          : '完成後客戶可直接用他自己設定的帳號密碼登入，不需要跑初始設定精靈。') +
        backup,
    })
  } catch (e) {
    await dialogs.notify({ title: '核可失敗', message: errText(e, '核可失敗（未留下半成品）') })
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <UiModal
    :open="open"
    :title="`${isPending ? '代管開站申請' : '申請詳情'} — ${customerName}`"
    width="820px"
    @close="close"
  >
    <template v-if="request">
      <!-- ① 客戶填的（唯讀） -->
      <h4 class="sub-title">客戶申請內容</h4>
      <div class="ro">
        <div class="ro-row">
          <span class="ro-k">產品</span>
          <span class="ro-v">{{ productName }}</span>
        </div>
        <div class="ro-row">
          <span class="ro-k">站台代號</span>
          <span class="ro-v"><code class="mono">{{ slug }}</code></span>
        </div>
        <div class="ro-row">
          <span class="ro-k">網域</span>
          <span class="ro-v">
            <code class="mono">{{ pre?.domain ?? '選擇主機後顯示' }}</code>
          </span>
        </div>
        <div class="ro-row">
          <span class="ro-k">站台名稱</span><span class="ro-v">{{ setup.site_name ?? '—' }}</span>
        </div>
        <div class="ro-row">
          <span class="ro-k">管理員</span>
          <span class="ro-v">{{ setup.admin_name ?? '—' }}（{{ setup.admin_email ?? '—' }}）</span>
        </div>
        <div class="ro-row">
          <span class="ro-k">登入方式</span><span class="ro-v">{{ loginLabel }}</span>
        </div>
        <div class="ro-row">
          <span class="ro-k">備註</span><span class="ro-v">{{ request.note || '—' }}</span>
        </div>
      </div>
      <p class="subtle">
        客戶的密碼我方只有 bcrypt 雜湊（明碼從未離開他的瀏覽器）。站台裝好後會自己向中台領走
        這份資料建立管理員——<strong>客戶不需要跑初始設定精靈</strong>。
      </p>
      <!-- 批141：反向隧道／代管 Google 登入照**產品的代管設定**（不再一律開）。 -->
      <p class="subtle">
        <template v-if="profileFeatures === undefined">
          開站時的反向隧道與代管 Google 登入依產品的代管設定（選擇主機後顯示）。
        </template>
        <template v-else-if="profileFeatures === null">
          這個產品<strong>沒有有效的代管設定</strong>，目前開不了站。
        </template>
        <template v-else-if="hasFeature(profileFeatures, 'tunnel') && hasFeature(profileFeatures, 'sso_google_managed')">
          開站時會<strong>啟用反向隧道</strong>並預設開啟「代管 Google 登入」
          ——代管登入的 OAuth callback 由隧道閘道代收，沒有反向隧道那個按鈕會是灰的。
        </template>
        <template v-else-if="hasFeature(profileFeatures, 'tunnel')">
          開站時會<strong>啟用反向隧道</strong>；這個產品不使用代管 Google 登入。
        </template>
        <template v-else-if="hasFeature(profileFeatures, 'sso_google_managed')">
          開站時會預設開啟「代管 Google 登入」；這個產品不使用反向隧道。
        </template>
        <template v-else>
          這個產品的站台<strong>不使用</strong>反向隧道，也不開代管 Google 登入。
        </template>
        <template v-if="setup.login === 'google'">
          這位客戶<strong>沒有設定密碼</strong>，只能用 Google 帳號登入。
        </template>
      </p>
      <p v-if="loginUnsupported" class="err">
        ⚠️ 客戶選的登入方式（{{ loginLabel }}）不在這個產品允許的範圍內，核可會被拒絕——
        請婉拒並請客戶依產品規定重新申請。
      </p>

      <!-- 已審核過＝唯讀，交代結果就好 -->
      <template v-if="!isPending">
        <h4 class="sub-title">審核結果</h4>
        <div class="ro">
          <div v-for="row in outcomeRows" :key="row.k" class="ro-row">
            <span class="ro-k">{{ row.k }}</span><span class="ro-v">{{ row.v }}</span>
          </div>
        </div>
      </template>

      <template v-else>
      <!-- ② 主機 -->
      <h4 class="sub-title">開在哪一台</h4>
      <label class="field">
        <span>主機</span>
        <select v-model="hostId">
          <option value="">— 請選擇 —</option>
          <option
            v-for="h in visibleHosts"
            :key="h.id"
            :value="h.id"
            :disabled="hostBlockers(h).length > 0"
          >
            {{ h.name }}（{{ h.sites_count }} 站{{ hostOwnerTag(h) }}{{
              hostBlockers(h).length ? `・${hostBlockers(h).join('、')}` : ''
            }}）
          </option>
        </select>
      </label>

      <div v-if="preBusy" class="subtle">容量檢查中…</div>
      <p v-else-if="preError" class="err">{{ preError }}</p>
      <template v-else-if="pre">
        <div v-if="pre.ok" class="okbox">✓ 開站前檢查全部通過</div>
        <div v-else class="blockbox">
          <strong>以下問題會讓開站被擋下</strong>
          <ul>
            <li v-for="i in pre.issues" :key="i">{{ i }}</li>
          </ul>
        </div>
        <div class="cap">
          <span>站數 {{ pre.host.sites_count }} / {{ pre.host.max_sites ?? '不限' }}</span>
          <span>
            記憶體
            {{ pre.host.mem_free_mb == null ? '未回報' : `${Math.round(pre.host.mem_free_mb)} MB 可用` }}
          </span>
          <span>
            資料碟
            {{ pre.host.disk_free_gb == null ? '未回報' : `${pre.host.disk_free_gb.toFixed(1)} GB 可用` }}
          </span>
          <span>LE 本週 {{ pre.le_weekly.used }} / {{ pre.le_weekly.budget }}</span>
          <span>channel <code class="mono">{{ pre.channel }}</code></span>
        </div>
      </template>

      <!-- ③ 授權 -->
      <h4 class="sub-title">授權內容</h4>
      <label class="cb">
        <input v-model="trial" type="checkbox" @change="onToggleTrial" />
        試用授權（到期自動停站；日後核可正式授權時會自動復站，資料不會被刪）
      </label>
      <div class="plan-row">
        <span class="plan-label">套裝</span>
        <!-- 批99-B：代理商只能照套裝模板簽，且該套裝要開放代理商免審核發；其餘灰掉並說明去處。 -->
        <button
          v-for="p in plans"
          :key="p.key"
          type="button"
          class="plan-chip"
          :class="{ on: plan === p.key, off: !planDirectOk(p.activation_policy) }"
          :disabled="!planDirectOk(p.activation_policy)"
          :title="planDirectOk(p.activation_policy) ? undefined : '需走授權申請流程'"
          @click="applyPlan(p.key)"
        >
          {{ p.label }}
          <span v-if="!planDirectOk(p.activation_policy)" class="chip-note">需走申請流程</span>
        </button>
        <!-- 「客製」：代理商不行；我方要營運主管以上（批151 開站內容閘：沒選套裝＝客製內容）。 -->
        <button
          type="button"
          class="plan-chip"
          :class="{ on: plan === '' && customPlanOk, off: !customPlanOk }"
          :disabled="!customPlanOk"
          :title="auth.isReseller ? '客製內容需走授權申請流程' : !customPlanOk ? '客製內容需「營運主管」以上' : undefined"
          @click="plan = ''"
        >
          客製
          <span v-if="auth.isReseller" class="chip-note">需走申請流程</span>
          <span v-else-if="!customPlanOk" class="chip-note">需營運主管</span>
        </button>
      </div>
      <p v-if="auth.isReseller" class="subtle">
        代理商直接核可只支援上面標示可用的套裝，且<strong>內容不可改動</strong>——模組、
        配額與期限若與模板不一致，送出時會被擋下（請改請客戶走授權申請流程）。
      </p>
      <!-- 批151 開站內容閘：admin 偏離／客製＝核可必 403 → 先講並擋住按鈕；營運主管以上只提示。 -->
      <p v-if="contentHint" :class="contentBlocked ? 'err' : 'subtle'">
        {{ contentBlocked ? '⚠️ ' : 'ⓘ ' }}{{ contentHint }}
      </p>
      <!-- 批114：獨立的數量欄已拔除（數量一律在下方「數量配額」填）；剩期限一欄，
           兩個控制項並排剛好一列。 -->
      <div class="form4">
        <label class="field">
          <span>期限</span>
          <!-- 代理商：只列後端允許的（套裝的期限 ∪ 產品的可選期限），
               否則選了 2 年會被政策閘擋下，而畫面上完全看不出哪裡不對。 -->
          <select v-if="auth.isReseller" v-model.number="termTotal">
            <option v-for="m in termChoices" :key="m" :value="m">{{ termLabel(m) }}</option>
          </select>
          <template v-else>
            <!-- 批108：我方視角多一個自由輸入（代管站也允許分鐘級的證，裁示 28）。 -->
            <select v-model.number="termTotal">
              <option :value="30 * MINUTES_PER_DAY">1 個月</option>
              <option :value="90 * MINUTES_PER_DAY">3 個月</option>
              <option :value="365 * MINUTES_PER_DAY">1 年</option>
              <option :value="730 * MINUTES_PER_DAY">2 年</option>
            </select>
            <TermField v-model="termTotal" />
          </template>
        </label>
      </div>
      <div class="mp">
        <ModulePicker :key="pickerKey" v-model="modules" :spec="spec" :allow-internal="auth.isManagerOrAbove" />
      </div>
      <h4 class="sub-title">數量配額</h4>
      <div class="mp">
        <QuotaEditor
          :key="quotaKey"
          v-model="quotas"
          :spec="spec"
          @update:valid="quotasValid = $event"
        />
      </div>

      <!-- 批151 C6：站台機密（產品有宣告＋營運主管以上；全部選填，只在按「核可並開站」時帶）。 -->
      <template v-if="showSecrets">
        <h4 class="sub-title">站台機密</h4>
        <SiteSecretInputs v-model="secretValues" :fields="secretFields" :blocker="secretBlocker" />
      </template>

      <!-- ④ 婉拒理由：**同框展開**（不再開第二層 modal），會顯示給客戶看 -->
      <div v-if="rejectOpen" class="rej-box">
        <label class="field">
          <span>婉拒理由（選填）</span>
          <input
            v-model="rejectReason"
            maxlength="500"
            placeholder="例：目前代管主機容量已滿，請於下月再提出。"
            v-enter="doReject"
          />
        </label>
        <p class="subtle">
          這段文字會顯示在客戶 portal 的「我的申請」列上，請用對客戶說得出口的措辭。留空＝不附理由。
        </p>
        <div class="rej-acts">
          <UiButton size="sm" @click="rejectOpen = false">取消婉拒</UiButton>
          <UiButton variant="danger" size="sm" :loading="rejecting" @click="doReject">
            確認婉拒
          </UiButton>
        </div>
      </div>
      </template>

      <!-- ⑤ 撤回（作廢）理由：**放在 pending 分支之外**——draft 狀態的死單也要撤得動 -->
      <div v-if="cancelOpen" class="rej-box">
        <label class="field">
          <span>撤回理由（{{ cancelReasonRequired ? '必填，客戶看得到' : '選填' }}）</span>
          <input
            v-model="cancelReason"
            maxlength="500"
            placeholder="例：已與客戶確認改為自建部署，這筆開站申請作廢。"
            v-enter="doCancel"
          />
        </label>
        <p class="subtle">
          撤回＝這筆不再處理，內容保留在「已取消」。這段理由會顯示在客戶 portal 的「我的申請」列上。
        </p>
        <div class="rej-acts">
          <UiButton size="sm" @click="cancelOpen = false">取消</UiButton>
          <UiButton
            variant="danger"
            size="sm"
            :disabled="cancelReasonRequired && !cancelReason.trim()"
            :loading="cancelling"
            @click="doCancel"
          >
            確認撤回
          </UiButton>
        </div>
      </div>
    </template>
    <template #foot>
      <UiButton @click="close">關閉</UiButton>
      <UiButton
        v-if="canCancel && !cancelOpen && !rejectOpen"
        variant="danger"
        :loading="cancelling"
        @click="cancelOpen = true"
      >
        撤回
      </UiButton>
      <template v-if="isPending">
        <UiButton v-if="!rejectOpen && !cancelOpen" variant="warn" @click="rejectOpen = true">
          婉拒
        </UiButton>
        <UiButton variant="primary" :disabled="!canSubmit" :loading="submitting" @click="submit">
          核可並開站
        </UiButton>
      </template>
    </template>
  </UiModal>
</template>

<style scoped>
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.sub-title:first-child {
  margin-top: 0;
}
.ro {
  display: grid;
  gap: 4px;
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
}
.ro-row {
  display: flex;
  gap: var(--sp-3);
  font-size: var(--fs-13);
}
.ro-k {
  color: var(--fg-3);
  min-width: 84px;
}
.ro-v {
  color: var(--fg);
  word-break: break-word;
}
/* 09-02：婉拒改成同框展開的區塊（不再開第二層 modal） */
.rej-box {
  margin-top: var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--danger);
  border-radius: var(--r-md);
  background: var(--bg);
}
.rej-box .field {
  display: grid;
  gap: 4px;
  margin-bottom: var(--sp-2);
}
.rej-acts {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
}
.cap {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin-top: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.okbox {
  margin-top: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--success, var(--brand-400));
  border-radius: var(--r-sm, 6px);
  color: var(--success, var(--brand-600));
  font-size: var(--fs-12);
}
.blockbox {
  margin-top: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--danger);
  border-radius: var(--r-sm, 6px);
  color: var(--danger);
  font-size: var(--fs-12);
}
.blockbox ul {
  margin: 4px 0 0;
  padding-left: 18px;
}
.err {
  color: var(--danger);
  font-size: var(--fs-12);
}
.cb {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-2);
}
.plan-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin: var(--sp-3) 0 0;
}
.plan-label {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.plan-chip {
  padding: 4px 12px;
  border: 1px solid var(--border-2);
  border-radius: 999px;
  background: transparent;
  color: var(--fg-2);
  font-size: var(--fs-13);
  cursor: pointer;
}
/* 不可直開的套裝（批99-B）：灰掉但仍看得到——要讓人知道有這個套裝、只是得走申請。 */
.plan-chip.off {
  opacity: 0.55;
  cursor: not-allowed;
}
.chip-note {
  margin-left: 6px;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.plan-chip.on {
  border-color: var(--brand-500);
  background: var(--brand-500);
  color: #fff;
}
/* 批114：拔掉兩個數量欄之後只剩期限一欄——改成網格，一欄至少 220px（`TermField`
   是「數字＋單位」兩個控制項，120px 的 flex 欄位會把它擠成兩行）。 */
.form4 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--sp-3);
  margin: var(--sp-3) 0;
}
.form4 .field {
  align-content: start;
}
.field {
  display: grid;
  gap: 4px;
}
.field span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.mp {
  margin-bottom: var(--sp-3);
}
</style>
